import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { InnerProcedureConfig } from '@core/models/built/procedure-step-config.model';
import { Procedure, ProcedureStep } from '@core/models/procedures.model';
import { ProcedureCoreService } from '@core/services/features/procedure-core.service';
import { FlowchartMode } from '@flowchart';
import { TranslocoService } from '@ngneat/transloco';
import { filterTruthy } from '@utils';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

import it from './i18n/inner-procedure-edit.it.json';

@Component({
  templateUrl: './inner-procedure-edit.component.html'
})
export class InnerProcedureEditComponent implements OnInit, OnChanges, OnDestroy {

  @Input() step?: ProcedureStep;
  @Input() mode: FlowchartMode = 'edit';
  @Input() editForm!: FormGroup;

  private search$$ = new Subject<string>();

  procedures$ = this.search$$.pipe(
    debounceTime(300),
    switchMap(value => this.procedureCoreSvc.getByName(value))
  );

  private step$$ = new BehaviorSubject<ProcedureStep | undefined>(undefined);
  step$ = this.step$$.pipe(filterTruthy());

  private selectedId$$ = new BehaviorSubject<number | undefined>(undefined);
  selectedId$ = this.selectedId$$.asObservable();

  constructor(
    private fb: FormBuilder,
    private procedureCoreSvc: ProcedureCoreService,
    private translocoSvc: TranslocoService
  ) {
    this.translocoSvc.setTranslation(it, 'it');
  }

  ngOnInit() {
    const config = this.step?.config as InnerProcedureConfig;

    const configGroup = (this.editForm.get('config') as FormGroup);
    if (configGroup) {
      configGroup.addControl('innerProcedureId', this.fb.control(config?.innerProcedureId, [Validators.required]));
    } else {
      this.editForm.addControl('config', this.fb.group({
        innerProcedureId: [config?.innerProcedureId, [Validators.required]]
      }));
    }

    this.selectedId$$.next(config?.innerProcedureId);

    if (this.mode === 'view') {
      (this.editForm.get('config') as FormGroup).disable();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.step) {
      this.step$$.next(changes.step.currentValue);
    }
  }

  ngOnDestroy() {
    const configGroup = (this.editForm.get('config') as FormGroup);
    if (configGroup) {
      configGroup.removeControl('innerProcedureId');

      if (Object.keys(configGroup.controls).length <= 0) {
        this.editForm.removeControl('config');
      }
    }
  }

  search(value?: string) {
    this.search$$.next(value);
  }

  autoCompleteDisplayWithFn(procedure: Procedure) {
    return procedure ? procedure.name : '';
  }

  select({ option: { value } }: MatAutocompleteSelectedEvent) {
    this.editForm.get('config.innerProcedureId')?.setValue(value?.id);
    this.selectedId$$.next(value?.id);
  }

  unselect() {
    this.editForm.get('config.innerProcedureId')?.setValue(undefined);
    this.selectedId$$.next(undefined);
  }
}
